// THIS IS A VIBECODED MESS. FAIR WARNINGS

import React from 'react';
import PropTypes from 'prop-types';

import {dispatch, connect} from 'utils/state_manager';

import {AdapterMoment, Alert, Box, ButtonBase, DateCalendar, LocalizationProvider, Popover, Stack, Typography} from 'views/components';
import {CalendarTodayOutlined} from 'views/icons';
import {Translate, translate} from 'views/module';

import SnapshotHeaderSection from './sections/snapshot_header/SnapshotHeaderSection';
import MonthlyKpisSection from './sections/monthly_kpis/MonthlyKpisSection';
import MonthlyFinancialOverviewSection from './sections/monthly_overview/MonthlyFinancialOverviewSection';
import ClientMonthlyBreakdownSection from './sections/client_monthly/ClientMonthlyBreakdownSection';
import MonthlyDataQualitySection from './sections/data_quality/MonthlyDataQualitySection';

import backend from 'utils/backend';
import moment from 'moment-timezone';

const TIMEZONE = 'America/Toronto';

// The aggregate monthly_snapshot response is an envelope of snapshot records; accept the
// documented {snapshots:[...]} shape as well as a bare array or single object.
const extractSnapshots = (response) => {
  if (!response) {
    return [];
  }

  if (Array.isArray(response.snapshots)) {
    return response.snapshots;
  }

  if (Array.isArray(response)) {
    return response;
  }

  return response.metricDate ? [response] : [];
};

const ClientMetrics = (props) => {
  const {
    statusProgressWrap: progressWrap,
  } = props;

  // Snapshots exist up to today in Toronto; future dates can never have one. All month
  // derivation comes from the snapshot itself, so the browser timezone is never consulted
  // for previous/current/next — only for bounding the picker at "today in Toronto".
  const latestAvailableDate = React.useMemo(() => {
    return moment.tz(TIMEZONE).startOf('day');
  }, []);

  const [selectedDate, setSelectedDate] = React.useState(latestAvailableDate.format('YYYY-MM-DD'));
  const [datePickerAnchorEl, setDatePickerAnchorEl] = React.useState(null);
  const [activePeriod, setActivePeriod] = React.useState('current');

  // Aggregate snapshot cache keyed by metricDate. Each snapshot already carries its own
  // previous/current/next periods, so a single date fetch feeds the whole dashboard.
  const [snapshotState, setSnapshotState] = React.useState({
    isLoading: true,
    hasLoadedOnce: false,
    errorMessage: '',
    byDate: {},
    fetchedDates: {},
  });

  // Per-client snapshots load independently from the aggregate above so a failure here
  // never blocks the rest of the dashboard. Cache is keyed by `<metricDate>#<month>`; each
  // entry accumulates its own paginated client rows.
  const [clientState, setClientState] = React.useState({
    cache: {},
    activeKey: '',
    isLoadingMore: false,
    errorMessage: '',
  });
  const clientRequestRef = React.useRef(0);

  React.useEffect(() => {
    let isActive = true;

    const loadSnapshot = async () => {
      // Route-local cache + dedup: a date we already fetched just clears the spinner.
      if (snapshotState.fetchedDates[selectedDate]) {
        setSnapshotState((currentState) => ({...currentState, isLoading: false}));

        return;
      }

      setSnapshotState((currentState) => ({...currentState, isLoading: true, errorMessage: ''}));

      await progressWrap(async () => {
        const [result] = await Promise.allSettled([
          backend.metrics.queryMonthlyFinancialSnapshots([selectedDate]),
        ]);

        if (!isActive) {
          return;
        }

        if (result.status === 'fulfilled') {
          const snapshots = extractSnapshots(result.value);

          setSnapshotState((currentState) => {
            const byDate = {...currentState.byDate};

            snapshots.forEach((snapshot) => {
              if (snapshot?.metricDate) {
                byDate[snapshot.metricDate] = snapshot;
              }
            });

            return {
              ...currentState,
              isLoading: false,
              hasLoadedOnce: true,
              errorMessage: '',
              byDate,
              fetchedDates: {...currentState.fetchedDates, [selectedDate]: true},
            };
          });

          return;
        }

        setSnapshotState((currentState) => ({
          ...currentState,
          isLoading: false,
          errorMessage: result.reason?.message || translate('metrics.ClientMetrics.Errors.LoadError'),
        }));
      });
    };

    loadSnapshot();

    return () => {
      isActive = false;
    };
    // fetchedDates is read above but intentionally omitted: selectedDate is the trigger and
    // re-running on every cache write would loop. Mirrors the client-detail effect below.
  }, [selectedDate, progressWrap]);

  const currentSnapshot = snapshotState.byDate[selectedDate] || null;
  // True whenever the snapshot for the *currently selected* date is still in flight, so
  // switching to an uncached date shows section skeletons rather than blank placeholders.
  const snapshotLoadingForDate = snapshotState.isLoading && !currentSnapshot;
  const isSettled = snapshotState.hasLoadedOnce && !snapshotState.isLoading && !snapshotState.errorMessage;
  const showMissingSnapshot = isSettled && Boolean(snapshotState.fetchedDates[selectedDate]) && !currentSnapshot;

  // The active period's calendar month comes from the snapshot, never derived locally.
  const activeMonth = currentSnapshot?.periods?.[activePeriod]?.month || '';

  const clientRequest = React.useMemo(() => {
    if (!currentSnapshot || !activeMonth) {
      return null;
    }

    return {metricDate: selectedDate, month: activeMonth, cacheKey: `${selectedDate}#${activeMonth}`};
  }, [currentSnapshot, activeMonth, selectedDate]);

  const loadClientSnapshot = React.useCallback(async ({metricDate, month, cacheKey, startKey = null, append = false}) => {
    const requestId = clientRequestRef.current + 1;
    clientRequestRef.current = requestId;

    setClientState((currentState) => ({
      ...currentState,
      activeKey: cacheKey,
      isLoadingMore: append,
      errorMessage: '',
    }));

    await progressWrap(async () => {
      let response = null;
      let failureMessage = '';

      try {
        response = await backend.metrics.queryMonthlyClientSnapshot({
          date: metricDate,
          month,
          lastEvaluatedKey: startKey || undefined,
        });
      } catch (error) {
        // Captured into section-local state rather than rethrown so progressWrap never
        // surfaces a global failure toast for this section.
        failureMessage = error?.message || translate('metrics.ClientMetrics.ClientMonthly.Errors.LoadError');
      }

      // A newer request (date/period change or another page) superseded this one.
      if (requestId !== clientRequestRef.current) {
        return;
      }

      if (failureMessage) {
        setClientState((currentState) => ({
          ...currentState,
          isLoadingMore: false,
          errorMessage: failureMessage,
        }));

        return;
      }

      setClientState((currentState) => {
        const existingEntry = currentState.cache[cacheKey];
        const previousClients = append && existingEntry ? existingEntry.clients : [];
        const seenClientRns = new Set(previousClients.map((client) => client.clientRn));
        const mergedClients = [...previousClients];

        (response?.clients || []).forEach((client) => {
          if (client && !seenClientRns.has(client.clientRn)) {
            seenClientRns.add(client.clientRn);
            mergedClients.push(client);
          }
        });

        // The backend states outright whether the snapshot exists (a TOTAL row was found),
        // which distinguishes "no snapshot for this date" from "snapshot ran, zero clients".
        const hasSnapshot = Boolean(response?.snapshotExists);

        return {
          ...currentState,
          isLoadingMore: false,
          errorMessage: '',
          cache: {
            ...currentState.cache,
            [cacheKey]: {
              clients: mergedClients,
              // Totals always come from the freshest full-snapshot response, never summed
              // from the paginated rows on screen.
              totals: response?.totals || existingEntry?.totals || null,
              lastEvaluatedKey: response?.lastEvaluatedKey || null,
              hasSnapshot: hasSnapshot || Boolean(existingEntry?.hasSnapshot),
            },
          },
        };
      });
    });
  }, [progressWrap]);

  React.useEffect(() => {
    if (!clientRequest) {
      return;
    }

    // A month already loaded just becomes active again without another round trip. A new
    // date or period yields a different cache key, which naturally resets pagination.
    if (clientState.cache[clientRequest.cacheKey]) {
      setClientState((currentState) => ({
        ...currentState,
        activeKey: clientRequest.cacheKey,
        isLoadingMore: false,
        errorMessage: '',
      }));

      return;
    }

    loadClientSnapshot(clientRequest);
    // clientState.cache read above is intentionally excluded from deps for the same reason
    // as the aggregate effect: the request key is the trigger.
  }, [clientRequest, loadClientSnapshot]);

  const handleLoadMore = React.useCallback(() => {
    if (!clientRequest) {
      return;
    }

    const entry = clientState.cache[clientRequest.cacheKey];

    if (!entry || !entry.lastEvaluatedKey || clientState.isLoadingMore) {
      return;
    }

    loadClientSnapshot({...clientRequest, startKey: entry.lastEvaluatedKey, append: true});
  }, [clientRequest, clientState, loadClientSnapshot]);

  const handleRetry = React.useCallback(() => {
    if (clientRequest) {
      loadClientSnapshot(clientRequest);
    }
  }, [clientRequest, loadClientSnapshot]);

  const clientEntry = clientRequest ? clientState.cache[clientRequest.cacheKey] || null : null;
  const clientIsActiveKey = Boolean(clientRequest) && clientState.activeKey === clientRequest.cacheKey;
  const clientErrorMessage = clientIsActiveKey ? clientState.errorMessage : '';
  // While the aggregate snapshot for this date is still loading, the breakdown shows its
  // own skeleton rather than a premature "missing snapshot".
  const clientIsLoading = snapshotLoadingForDate || (Boolean(clientRequest) && !clientEntry && !clientErrorMessage);
  const clientIsLoadingMore = clientIsActiveKey && clientState.isLoadingMore;

  const formatDisplayDate = (date) => {
    return moment.tz(date, 'YYYY-MM-DD', TIMEZONE).format('MMM D, YYYY');
  };

  const generatedAtText = currentSnapshot?.generatedAt ?
    translate('metrics.ClientMetrics.GeneratedAt', {
      timestamp: moment.tz(currentSnapshot.generatedAt, TIMEZONE).format('MMM D, YYYY h:mm A'),
    }) :
    '';

  // Render the data sections while loading (skeletons) or once a snapshot exists. A settled
  // date with no snapshot collapses to just the "missing snapshot" alert rather than a wall
  // of em-dash placeholders. A hard first-load error suppresses the sections entirely.
  const hadFirstLoadError = Boolean(snapshotState.errorMessage) && !snapshotState.hasLoadedOnce;
  const renderDataSections = !hadFirstLoadError && (Boolean(currentSnapshot) || snapshotLoadingForDate);

  return (
    <Box
      sx={{
        minHeight: '100%',
        backgroundColor: '#F9F8F6',
      }}
    >
      <Box
        sx={{
          width: '100%',
          maxWidth: '1160px',
          mx: 'auto',
          px: {xs: 2, md: 4},
          py: {xs: 3, md: 5.5},
        }}
      >
        <Stack spacing={3}>
          <Stack
            direction={{xs: 'column', lg: 'row'}}
            justifyContent='space-between'
            alignItems={{xs: 'flex-start', lg: 'center'}}
            spacing={2}
          >
            <Box sx={{minWidth: 0}}>
              <Typography
                sx={{
                  fontSize: {xs: '24px', md: '28px'},
                  lineHeight: '36px',
                  color: '#1A1916',
                  mb: 1,
                }}
              >
                <Translate expression='metrics.ClientMetrics.Title' />
              </Typography>
              <Typography
                sx={{
                  fontSize: '13px',
                  lineHeight: '17px',
                  color: '#A8A49E',
                }}
              >
                <Translate expression='metrics.ClientMetrics.Subtitle' />
              </Typography>
              {
                generatedAtText &&
                <Typography
                  sx={{
                    fontSize: '11px',
                    lineHeight: '15px',
                    color: '#A8A49E',
                    mt: 0.75,
                  }}
                >
                  {generatedAtText}
                </Typography>
              }
            </Box>
            <Stack direction='row' alignItems='center' spacing={1.5} flexWrap='wrap' useFlexGap>
              <ButtonBase
                onClick={(event) => setDatePickerAnchorEl(event.currentTarget)}
                aria-label={translate('metrics.ClientMetrics.DatePickerLabel')}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  px: 1.25,
                  py: 0.75,
                  backgroundColor: '#FFFFFF',
                  border: '1px solid #EBEBEB',
                  borderRadius: '8px',
                  cursor: 'pointer',
                }}
              >
                <CalendarTodayOutlined sx={{fontSize: 16, color: '#919191'}} />
                <Typography
                  sx={{
                    fontSize: '11px',
                    fontWeight: 500,
                    color: '#6B6761',
                  }}
                >
                  {formatDisplayDate(selectedDate)}
                </Typography>
              </ButtonBase>
              {
                datePickerAnchorEl &&
                <Popover
                  open={Boolean(datePickerAnchorEl)}
                  anchorEl={datePickerAnchorEl}
                  onClose={() => setDatePickerAnchorEl(null)}
                  anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                  }}
                  transformOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                  }}
                  slotProps={{
                    paper: {
                      sx: {
                        mt: 1,
                        borderRadius: '12px',
                        border: '1px solid #E8E4DE',
                        boxShadow: '0px 12px 32px rgba(26, 25, 22, 0.12)',
                      },
                    },
                  }}
                >
                  <Box sx={{p: 1}}>
                    <LocalizationProvider dateAdapter={AdapterMoment}>
                      <DateCalendar
                        value={moment.tz(selectedDate, 'YYYY-MM-DD', TIMEZONE)}
                        maxDate={latestAvailableDate}
                        onChange={(nextDate) => {
                          if (!nextDate?.isValid?.()) {
                            return;
                          }

                          setSelectedDate(nextDate.clone().tz(TIMEZONE).format('YYYY-MM-DD'));
                          setDatePickerAnchorEl(null);
                        }}
                      />
                    </LocalizationProvider>
                  </Box>
                </Popover>
              }
            </Stack>
          </Stack>

          {
            snapshotState.errorMessage &&
            !snapshotState.isLoading &&
            <Alert severity='warning'>
              {snapshotState.errorMessage}
            </Alert>
          }
          {
            showMissingSnapshot &&
            <Alert severity='info'>
              <Translate
                expression='metrics.ClientMetrics.EmptyStates.MissingSnapshot'
                substitute={{date: formatDisplayDate(selectedDate)}}
              />
            </Alert>
          }

          {
            renderDataSections &&
            <>
              <SnapshotHeaderSection
                isLoading={snapshotLoadingForDate}
                snapshot={currentSnapshot}
              />

              <MonthlyKpisSection
                isLoading={snapshotLoadingForDate}
                snapshot={currentSnapshot}
              />

              <MonthlyFinancialOverviewSection
                isLoading={snapshotLoadingForDate}
                snapshot={currentSnapshot}
              />

              <ClientMonthlyBreakdownSection
                activePeriod={activePeriod}
                onPeriodChange={setActivePeriod}
                cacheKey={clientRequest?.cacheKey || ''}
                isLoading={clientIsLoading}
                isLoadingMore={clientIsLoadingMore}
                errorMessage={clientErrorMessage}
                month={activeMonth}
                metricDate={selectedDate}
                clients={clientEntry?.clients || []}
                totals={clientEntry?.totals || null}
                hasSnapshot={Boolean(clientEntry?.hasSnapshot)}
                hasMore={Boolean(clientEntry?.lastEvaluatedKey)}
                onLoadMore={handleLoadMore}
                onRetry={handleRetry}
              />

              <MonthlyDataQualitySection
                isLoading={snapshotLoadingForDate}
                snapshot={currentSnapshot}
              />
            </>
          }
        </Stack>
      </Box>
    </Box>
  );
};

ClientMetrics.propTypes = {
  statusProgressWrap: PropTypes.func.isRequired,
};

const mapStateToProps = (state) => ({
});

export default connect(mapStateToProps, dispatch)(React.memo(ClientMetrics));
